import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { agregarTarea } from "../../../features/registroTareasSlice";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Material UI y Lucide
import { 
  Box, Typography, TextField, Grid, Button, Paper, Stack 
} from "@mui/material";
import { PlusCircle, FileText, Calendar, Globe, AlignLeft } from "lucide-react";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = { 
  ice: "#C2EDF0", 
  aqua: "#00C0CA", 
  teal: "#008B9C", 
  dark: "#22404D", 
  slate: "#94A3B8",
  pearl: "#EEF3F4"
};

const RevisionTexto = ({ idPedido }) => {
    // ==========================================
    // LÓGICA ORIGINAL (MANTENIDA)
    // ==========================================
    const tareas = useSelector(state => state.tareas.Tareas);
    const dispatch = useDispatch();
    const [formRevisionTexto, setFormRevisionTexto] = useState({
        tipoTarea: "RevisionTexto",
        fechaLimite: "",
        texto: "",
        plataforma: "",
        estado: "Pendiente"
    });

    const [mostrarFormulario, setMostrarFormulario] = useState(true);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormRevisionTexto({
            ...formRevisionTexto,
            [name]: value
        });
    };

    const validar = () => {
        const { fechaLimite, texto, plataforma } = formRevisionTexto;
        const fechaFormulario = new Date(fechaLimite);
        const fechaActual = new Date();
        fechaActual.setHours(0, 0, 0, 0);
        fechaFormulario.setHours(0, 0, 0, 0);

        if (!texto) return "El texto no puede estar vacío.";
        if (!fechaLimite) return "La fecha límite no puede estar vacía.";
        if (fechaFormulario < fechaActual) return "La fecha límite debe ser una fecha futura.";
        if (!plataforma) return "La plataforma no puede estar vacía.";
        return ""; 
    };

    const agregarTareaAlPedido = async () => {
        const mensaje = validar();
        if (mensaje !== "") {
            toast.error(mensaje, { position: "bottom-center", autoClose: 3000 });
            return;
        }

        const { fechaLimite, texto, plataforma } = formRevisionTexto;
        const registro = {
            id: tareas.length + 1,
            tipoTarea: "RevisionTexto",
            comentario:"",
            fechaLimite,
            texto,
            plataforma,
            estado: "Pendiente"
        };

        if (idPedido) {
            const respuesta = await fetch(`${apiUrl}/Tarea/AgregarTareaRevisionTexto?IdPedido= `+ idPedido, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'apiKey': localStorage.getItem("apiKey")
                },
                body: JSON.stringify(registro)
            });

            if (respuesta.ok) {
                setMostrarFormulario(false);
                toast.success('Pedido agregado correctamente', { position: "bottom-center", autoClose: 3000 });
            }
        } else {
            dispatch(agregarTarea(registro));
            setMostrarFormulario(false);
            toast.success('Tarea agregada correctamente', { position: "bottom-center", autoClose: 3000 });
        }
    };
    // ==========================================

    if (!mostrarFormulario) return null;

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                p: 3, 
                borderRadius: '24px', 
                bgcolor: PALETTE.pearl, 
                border: `1px solid ${PALETTE.ice}`,
                mt: 2,
                animation: 'fadeIn 0.3s ease-in-out'
            }}
        >
            <Stack spacing={2.5}>
                <Box display="flex" alignItems="center" gap={1}>
                    <FileText size={20} color={PALETTE.teal} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: PALETTE.dark }}>
                        Revisión de Texto
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>TEXTO A REVISAR</Typography>
                        <TextField 
                            fullWidth 
                            multiline
                            rows={4}
                            name="texto" 
                            value={formRevisionTexto.texto} 
                            onChange={handleChange} 
                            placeholder="Pega aquí el contenido que requiere corrección o revisión..."
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '16px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>FECHA LÍMITE</Typography>
                        <TextField 
                            fullWidth 
                            type="date" 
                            name="fechaLimite" 
                            value={formRevisionTexto.fechaLimite} 
                            onChange={handleChange} 
                            inputProps={{ min: new Date().toISOString().split('T')[0] }}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>PLATAFORMA / DESTINO</Typography>
                        <TextField 
                            fullWidth 
                            name="plataforma" 
                            value={formRevisionTexto.plataforma} 
                            onChange={handleChange} 
                            placeholder="Ej: Web, Prensa, Newsletter"
                            InputProps={{ startAdornment: <Globe size={16} style={{marginRight: 8, color: PALETTE.slate}} /> }}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>
                </Grid>

                <Button 
                    variant="contained" 
                    fullWidth 
                    onClick={agregarTareaAlPedido}
                    startIcon={<PlusCircle size={18} />}
                    sx={{ 
                        mt: 1, 
                        borderRadius: '12px', 
                        py: 1.2, 
                        fontWeight: 800, 
                        bgcolor: PALETTE.teal,
                        '&:hover': { bgcolor: PALETTE.dark }
                    }}
                >
                    Añadir Revisión
                </Button>
            </Stack>
        </Paper>
    );
};

export default RevisionTexto;